import { ActiveSourcingChannelDetails } from 'api-clients/monolith';
import { Calendar1Icon, PencilIcon } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { Banner } from '@/components/ui/banner';
import { Button } from '@/components/ui/button';
import { BOOST, OTHER, POOL, REACH, ZIPRECRUITER } from '@/types/channels';
import { isFreeChannel } from './channel-utils';

export function CampaignDataUpdateBanner({
  campaignData,
}: CampaignDataUpdateBannerProps) {
  const intl = useIntl();
  const isFree = isFreeChannel(
    campaignData.sourcing_channel,
    campaignData.budget_stats.expected == null,
    campaignData.contract_id == null,
  );

  const {
    sourcing_purchase_id: sourcingPurchaseId,
    end_at: endAt,
    budget_stats: { expected: expectedBudget },
    sourcing_channel: { slug: channelSlug, is_vonq_channel: isVonqChannel },
    status,
  } = campaignData;

  const isReachCampaign = channelSlug === REACH;
  const restrictedChannels = [BOOST, OTHER, POOL, ZIPRECRUITER];

  const showBanner =
    status !== 'completed' &&
    (!endAt || !expectedBudget) &&
    !restrictedChannels.includes(channelSlug) &&
    !isVonqChannel &&
    !isFree;

  if (!showBanner) return null;

  const missingEndAt = !endAt;
  const missingBudget = !expectedBudget;

  const title = (() => {
    if (isReachCampaign) {
      return intl.formatMessage({
        defaultMessage: 'Add end date to prevent overspending',
        description:
          'Title of the banner when the end date is missing for a Reach campaign',
      });
    }
    if (missingEndAt && missingBudget) {
      return intl.formatMessage({
        defaultMessage:
          'Improve campaign estimates by adding a budget and end date',
        description:
          'Title of the banner when the budget and end date are missing',
      });
    }
    if (missingEndAt) {
      return intl.formatMessage({
        defaultMessage: 'Improve campaign estimates by adding an end date',
        description: 'Title of the banner when the end date is missing',
      });
    }
    return intl.formatMessage({
      defaultMessage: 'Improve campaign estimates by adding a budget',
      description: 'Title of the banner when the budget is missing',
    });
  })();

  const description = (() => {
    if (isReachCampaign && missingEndAt) {
      return intl.formatMessage({
        defaultMessage:
          'Without an end date, this campaign will run indefinitely and may exceed your budget.',
        description:
          'Description of the banner when the end date is missing for a Reach campaign',
      });
    }

    return intl.formatMessage({
      defaultMessage:
        'See your estimated spend and cost-per-applicant as this campaign runs.',
      description:
        'Description of the banner when the budget and end date are missing',
    });
  })();

  return (
    <Banner
      color="neutral"
      title={title}
      description={description}
      borderLess
      icon={<Calendar1Icon className="text-fx-neutral-600 size-4" />}
      className="[&>div>p:last-child]:!text-fx-neutral-600 rounded-none p-2"
      button={
        <Button asChild variant="secondary">
          <Link to={`${channelSlug}/${sourcingPurchaseId ?? ''}/edit`}>
            <PencilIcon className="text-fx-neutral-900" />
            <FormattedMessage
              defaultMessage="Update details"
              description="CTA button to edit the campaign details"
            />
          </Link>
        </Button>
      }
    />
  );
}

interface CampaignDataUpdateBannerProps {
  campaignData: ActiveSourcingChannelDetails;
}
